import { useState, useMemo, useEffect, useContext, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import AppBackService from "../services/appback";
import { retryTapImageOtherExt } from "../services/utilities";
import { RootContext } from "../context/root-provider";
import { Link, useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorIcon from "@mui/icons-material/Error";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaletteIcon from "@mui/icons-material/Palette";
import SensorsIcon from "@mui/icons-material/Sensors";
import YouTubeIcon from "@mui/icons-material/YouTube";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import AddIcon from "@mui/icons-material/Add";
import ShareIcon from "@mui/icons-material/Share";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CreateTapModal from "../components/CreateTapModal";
import UploadAudioModal from "../components/UploadAudioModal";
import ShareModal from "../components/ShareModal";
import DocShareModal from "../components/DocShareModal";
import { canEditDoc, sharedByLabel } from "../components/docSharing";
import PendingShares from "../components/PendingShares";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import { useSnackbar } from "../components/AlertNotification";
import { useDocumentActions, useTapActions } from "../hooks/hooks";
import emptyDocsImg from "../assets/blank_docs.png";
import useFeatureFlag from "../hooks/useFeatureFlag";
import { SOURCE_LINK_KIND } from "../vendor/liveTapLinks/sourceLink";
import { useDropAnything } from "../context/DropAnythingContext";
import { usePageHeader } from "../context/PageHeaderContext";
import { canRetry } from "../services/docRetry";
import { spokenFacts } from "../services/spokenDocs";
import styles from "./Unified.module.scss";

const TAPS_PER_ROW = 4;
const PAGE_SIZE = 20;

function docName() {
  return "Document - " + dayjs().format("llll");
}

const UnifiedDocumentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [docs, setDocs] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("docViewMode") || "grid");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadDocName, setUploadDocName] = useState(docName());
  const [uploadTextfield, setUploadTextfield] = useState("");
  const [createTapModalOpen, setCreateTapModalOpen] = useState(false);
  const [creatingDoc, setCreatingDoc] = useState();
  // The doc stays set after close so the dialog's fade-out keeps its title.
  const [sharingDoc, setSharingDoc] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [uploadAudioModalOpen, setUploadAudioModalOpen] = useState(false);
  const [retryingDocIds, setRetryingDocIds] = useState(() => new Set());
  const sentinelRef = useRef(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const isMobile = useMediaQuery("(max-width:899px)");

  const { userInfo, loggingIn, notifInterval } = useContext(RootContext);
  const { showSnackbar, snackbarEl } = useSnackbar();
  const tapestryTeamFeature = useFeatureFlag(userInfo);

  // The start modals and the link routing are app-wide (TT-1231,
  // app/DropAnything.js); the buttons here reach them, and the list refetches
  // after a start while this page is up.
  const { startFromButton, registerRefresh } = useDropAnything();

  // document action handlers

  const updateTitle = useCallback(
    (title, docId) => {
      if (!title) {
        showSnackbar("Unable to update title", "error");
      } else {
        setDocs((docs) => {
          const docsCopy = [...docs];
          const updatedDoc = docsCopy.find((doc) => doc.docId === docId);
          updatedDoc.title = title;
          return docsCopy;
        });
        showSnackbar("Document title updated", "success");
      }
    },
    [showSnackbar]
  );

  const resolveDelete = useCallback(
    (docId) => {
      if (!docId) {
        showSnackbar("Unable to delete document", "error");
      } else {
        setDocs((docs) => docs.filter((doc) => doc.docId !== docId));
        showSnackbar("Document deleted", "success");
      }
    },
    [showSnackbar]
  );

  const { setDeleting, deleteConfirm, setEditingTitle, editTitleDialog } = useDocumentActions(
    updateTitle,
    resolveDelete
  );

  const resolveTapDelete = useCallback(
    (tapId) => {
      if (!tapId) {
        showSnackbar("Unable to delete Tapestry", "error");
      } else {
        setDocs((docs) => {
          return docs.map((doc) => {
            const tapIndex = doc.taps.findIndex((tap) => tap.tapId === tapId);
            if (tapIndex >= 0) {
              const newTaps = [...doc.taps];
              newTaps.splice(tapIndex, 1);
              return { ...doc, taps: newTaps };
            }
            return doc;
          });
        });
        showSnackbar("Tapestry deleted", "success");
      }
    },
    [showSnackbar]
  );

  const { setDeletingTap, deleteTapConfirm } = useTapActions(resolveTapDelete);

  const deleteTap = useCallback(
    (tapId, title) => {
      setDeletingTap({ title, tapId });
    },
    [setDeletingTap]
  );

  const openShare = useCallback((doc) => {
    setSharingDoc(doc);
    setShareOpen(true);
  }, []);

  const editTitle = useCallback(
    (title, docId) => {
      setEditingTitle({ title, docId });
    },
    [setEditingTitle]
  );

  const deleteDoc = useCallback(
    (title, docId) => {
      setDeleting({ title, docId });
    },
    [setDeleting]
  );

  const processDocs = useCallback((rawDocs) => {
    return rawDocs.map((doc) => {
      doc.created = dayjs(isNaN(+doc.created) ? doc.created : +doc.created);
      return doc;
    });
  }, []);

  const fetchDocs = useCallback(
    async (showLoading = true, pageNum = 0) => {
      if (userInfo?.username) {
        if (showLoading) {
          setLoading(true);
        }
        if (pageNum > 0) {
          setLoadingMore(true);
        }
        const data = await AppBackService.getUserDocs(userInfo.username, {
          limit: PAGE_SIZE,
          offset: pageNum * PAGE_SIZE,
        });
        if (!data || data.error) {
          setError(true);
        } else {
          setError(false);
          const newDocs = data.docs?.length ? processDocs(data.docs) : [];
          newDocs.sort((a, b) => (a.created < b.created ? 1 : -1));
          if (pageNum === 0) {
            setDocs(newDocs);
          } else {
            setDocs((prev) => [...prev, ...newDocs]);
          }
          setHasMore(newDocs.length >= PAGE_SIZE);
          // What the doc model says about the page's docs (failure, source),
          // in one read once the rows are up (TT-1273).
          spokenFacts(
            userInfo.username,
            newDocs.map((d) => d.docId)
          ).then((facts) => {
            if (facts.size) {
              setDocs((docs) => docs.map((d) => (facts.has(d.docId) ? { ...d, spoken: facts.get(d.docId) } : d)));
            }
          });
        }
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [userInfo, processDocs]
  );

  const retryDoc = useCallback(
    async (doc) => {
      if (!userInfo?.username || !doc?.docId || retryingDocIds.has(doc.docId)) return;

      setRetryingDocIds((ids) => new Set(ids).add(doc.docId));
      try {
        const resp = await AppBackService.retryUserDoc(userInfo.username, doc.docId);
        if (resp && !resp.error) {
          showSnackbar("Retry started", "success");
          setDocs((docs) =>
            docs.map((item) =>
              item.docId === doc.docId ? { ...item, spoken: item.spoken && { ...item.spoken, failure: null } } : item
            )
          );
          setPage(0);
          fetchDocs(false, 0);
          return;
        }
        showSnackbar("Unable to retry document", "error");
      } catch (error) {
        showSnackbar("Unable to retry document", "error");
      } finally {
        setRetryingDocIds((ids) => {
          const next = new Set(ids);
          next.delete(doc.docId);
          return next;
        });
      }
    },
    [fetchDocs, retryingDocIds, showSnackbar, userInfo?.username]
  );

  // init
  useEffect(() => {
    if (userInfo?.username && !loggingIn) {
      fetchDocs(true, 0);
    }
  }, [fetchDocs, loggingIn, userInfo]);

  // A start made anywhere (a button here, a dropped link) refetches the list.
  useEffect(
    () =>
      registerRefresh(() => {
        setPage(0);
        fetchDocs(false, 0);
      }),
    [registerRefresh, fetchDocs]
  );

  // infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchDocs(false, nextPage);
        }
      },
      { rootMargin: "200px" }
    );

    const el = sentinelRef.current;
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [hasMore, loading, loadingMore, page, fetchDocs]);

  // when search filter is active, auto-load remaining pages so search covers all docs
  useEffect(() => {
    if (filter && hasMore && !loading && !loadingMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchDocs(false, nextPage);
    }
  }, [filter, hasMore, loading, loadingMore, page, fetchDocs]);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const next = prev === "grid" ? "list" : "grid";
      localStorage.setItem("docViewMode", next);
      return next;
    });
  }, []);

  const { setHeaderContent } = usePageHeader();

  // Inject header content into the navbar
  useEffect(() => {
    setHeaderContent(
      <div className={styles.navHeaderContent}>
        <TextField
          placeholder="Search Tapestries..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 280,
            "& .MuiOutlinedInput-root": {
              height: 36,
              fontSize: "0.875rem",
              backgroundColor: "rgba(255,255,255,0.12) !important",
              color: "var(--color-nav-text)",
              paddingLeft: "10px",
              "& fieldset": {
                borderColor: "rgba(255,255,255,0.15)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.3)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,255,255,0.5)",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "8px 12px 8px 0",
              background: "transparent !important",
              "&::placeholder": {
                color: "rgba(255,255,255,0.6) !important",
                opacity: "1 !important",
              },
            },
          }}
        />
        <button
          className={`${styles.viewToggle} ${viewMode === "grid" ? styles.viewToggleActive : ""}`}
          onClick={toggleViewMode}
          title={viewMode === "grid" ? "Switch to list view" : "Switch to grid view"}>
          {viewMode === "grid" ? <ViewListIcon /> : <ViewModuleIcon />}
        </button>
        <div className={styles.navActions}>
          {isMobile ? (
            <>
              <button className={styles.addMenuBtn} onClick={(e) => setAddMenuAnchor(e.currentTarget)} title="Create">
                <AddIcon />
              </button>
              <Menu
                anchorEl={addMenuAnchor}
                open={!!addMenuAnchor}
                onClose={() => setAddMenuAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <MenuItem onClick={() => { setAddMenuAnchor(null); setUploadModalOpen(true); }}>
                  <ListItemIcon sx={{ color: "inherit" }}><UploadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Upload Document</ListItemText>
                </MenuItem>
                {tapestryTeamFeature && [
                  <MenuItem key="yt" onClick={() => { setAddMenuAnchor(null); startFromButton(SOURCE_LINK_KIND.YOUTUBE); }}>
                    <ListItemIcon sx={{ color: "inherit" }}><YouTubeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>YouTube</ListItemText>
                  </MenuItem>,
                  <MenuItem key="xs" onClick={() => { setAddMenuAnchor(null); startFromButton(SOURCE_LINK_KIND.XSPACE); }}>
                    <ListItemIcon sx={{ color: "inherit" }}><GraphicEqIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>X Space</ListItemText>
                  </MenuItem>,
                  <MenuItem key="au" onClick={() => { setAddMenuAnchor(null); setUploadAudioModalOpen(true); }}>
                    <ListItemIcon sx={{ color: "inherit" }}><AudioFileIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Audio</ListItemText>
                  </MenuItem>,
                ]}
              </Menu>
            </>
          ) : (
            <>
              <button className={styles.actionBtn} onClick={() => setUploadModalOpen(true)} title="Upload Document">
                <UploadIcon /> Upload
              </button>
              {tapestryTeamFeature && (
                <>
                  <button className={styles.actionBtnSecondary} onClick={() => startFromButton(SOURCE_LINK_KIND.YOUTUBE)} title="YouTube Transcription">
                    <YouTubeIcon /> YouTube
                  </button>
                  <button className={styles.actionBtnSecondary} onClick={() => startFromButton(SOURCE_LINK_KIND.XSPACE)} title="X Space Transcription">
                    <GraphicEqIcon /> X Space
                  </button>
                  <button className={styles.actionBtnSecondary} onClick={() => setUploadAudioModalOpen(true)} title="Upload Audio">
                    <AudioFileIcon /> Audio
                  </button>
                </>
              )}
            </>
          )}
          <button className={`${styles.actionBtn} ${styles.liveBtn}`} onClick={() => startFromButton()} title="Start Live Tapestry">
            <PaletteIcon /> <span className={styles.liveBtnLabel}>Live</span>
          </button>
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [filter, viewMode, tapestryTeamFeature, setHeaderContent, toggleViewMode, isMobile, addMenuAnchor, startFromButton]);

  const openUploadModal = useCallback(() => setUploadModalOpen(true), []);
  const closeUploadModal = useCallback(() => setUploadModalOpen(false), []);

  useEffect(() => {
    if (!uploadModalOpen) {
      setUploadDocName(docName());
      setUploadTextfield("");
    }
  }, [uploadModalOpen]);

  const createDefaultTap = useCallback(
    async (docId) => {
      if (docId) {
        const resp = await AppBackService.createUserTap(userInfo?.username, { doc_id: docId });
        if (resp && !resp.error) {
          showSnackbar("Creating Tapestry...", "info");
        } else {
          showSnackbar("Error creating Tapestry", "error");
        }
        notifInterval(() => fetchDocs(false, 0), showSnackbar, 10000, 6, false);
      }
    },
    [fetchDocs, notifInterval, showSnackbar, userInfo?.username]
  );

  const uploadDocuments = useCallback(
    (files) => {
      if (!files.length) return;
      for (const file of files) {
        const title = file.name.match(/^([^\\]*)\.[^.]+$/)?.[1] || new Date().toLocaleString();
        const fr = new FileReader();
        fr.onload = (e) => {
          async function postFiles() {
            const resp = await AppBackService.uploadDoc(userInfo?.username, {
              title,
              file_name: file.name,
              file_data: window.btoa(e.target.result),
            });
            if (!resp || resp.error) {
              showSnackbar("Unable to upload document", "error");
            } else {
              showSnackbar(`Document ${title} uploaded`, "success");
              if (userInfo?.username && !loggingIn) {
                setPage(0);
                fetchDocs(false, 0);
              }
              if (resp.docId) createDefaultTap(resp.docId);
            }
            closeUploadModal();
          }
          postFiles();
        };
        fr.onerror = () => showSnackbar("Unable to upload document", "error");
        showSnackbar(`Uploading document: ${file.name}`, "info");
        fr.readAsBinaryString(file);
      }
    },
    [closeUploadModal, createDefaultTap, fetchDocs, loggingIn, showSnackbar, userInfo]
  );

  const uploadDocumentText = useCallback(async () => {
    const resp = await AppBackService.uploadDoc(userInfo?.username, {
      title: uploadDocName,
      content: uploadTextfield,
    });
    if (!resp || resp.error) {
      showSnackbar("Unable to upload document", "error");
    } else {
      showSnackbar(`Document ${uploadDocName} uploaded`, "success");
      if (userInfo?.username && !loggingIn) {
        setPage(0);
        fetchDocs(false, 0);
      }
      if (resp.docId) createDefaultTap(resp.docId);
    }
    closeUploadModal();
  }, [closeUploadModal, createDefaultTap, fetchDocs, loggingIn, showSnackbar, uploadDocName, uploadTextfield, userInfo]);

  // filtered docs
  const filteredDocs = useMemo(
    () =>
      docs.filter(
        (doc) =>
          !filter ||
          doc.title.toLowerCase().includes(filter.toLowerCase()) ||
          doc.created.format("llll").toLowerCase().includes(filter.toLowerCase())
      ),
    [docs, filter]
  );

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <ErrorIcon />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pendingSlot}>
        <PendingShares
          onAccepted={() => {
            setPage(0);
            fetchDocs(false, 0);
          }}
          showSnackbar={showSnackbar}
        />
      </div>
      {docs.length || loading ? (
        <div className={styles.pageContent}>
          {/* Document cards / list */}
          <div className={viewMode === "list" ? styles.docsListContainer : styles.docsContainer}>
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <div className={styles.skeleton} key={i} />)
              : filteredDocs.length
              ? filteredDocs.map((doc) =>
                  viewMode === "list" ? (
                    <DocumentRow
                      key={doc.docId}
                      doc={doc}
                      deleteDoc={deleteDoc}
                      editTitle={editTitle}
                      setCreateTapModalOpen={setCreateTapModalOpen}
                      setCreatingDoc={setCreatingDoc}
                      shareDoc={openShare}
                      retryDoc={retryDoc}
                      retrying={retryingDocIds.has(doc.docId)}
                    />
                  ) : (
                    <DocumentCard
                      key={doc.docId}
                      doc={doc}
                      deleteDoc={deleteDoc}
                      editTitle={editTitle}
                      setCreateTapModalOpen={setCreateTapModalOpen}
                      setCreatingDoc={setCreatingDoc}
                      shareDoc={openShare}
                      deleteTap={deleteTap}
                      retryDoc={retryDoc}
                      retrying={retryingDocIds.has(doc.docId)}
                    />
                  )
                )
              : <div className={styles.loadingMore}>No documents found</div>
            }
          </div>

          {/* Infinite scroll sentinel */}
          {!loading && hasMore && <div ref={sentinelRef} className={styles.scrollSentinel} />}
          {loadingMore && <div className={styles.loadingMore}>Loading more...</div>}

          <CreateTapModal
            open={createTapModalOpen}
            setOpen={setCreateTapModalOpen}
            doc={creatingDoc}
            refreshCallback={() => { setPage(0); fetchDocs(false, 0); }}
            showSnackbar={showSnackbar}
          />
          <DocShareModal
            open={shareOpen}
            handleClose={() => setShareOpen(false)}
            doc={sharingDoc}
            showSnackbar={showSnackbar}
          />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyHeading}>Ready to make visual magic with your words?</div>
          <img className={styles.emptyImg} src={emptyDocsImg} alt="symbols representing visual magic" />
          <div className={styles.emptySubheading}>Upload a document to create a Tapestry.</div>
          <button className={styles.actionBtn} onClick={openUploadModal}>
            <UploadIcon /> Upload Document
          </button>
          <div className={styles.emptySubheading} style={{ marginTop: "32px" }}>
            Or, start a live session to create a Tapestry.
          </div>
          <button className={styles.actionBtn} onClick={() => startFromButton()}>
            <PaletteIcon /> Live Tapestry
          </button>
        </div>
      )}

      {/* Upload modal */}
      <Dialog fullWidth maxWidth="sm" open={uploadModalOpen} onClose={closeUploadModal}>
        <DialogTitle>
          Upload Document
          <IconButton aria-label="close" onClick={closeUploadModal} sx={{ position: "absolute", right: 8, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ marginBottom: "8px", fontWeight: "bold" }}>Attach a document</Typography>
          <Dropzone
            onDrop={(files) => uploadDocuments(files)}
            multiple={false}
            accept={{
              "text/plain": [".txt"],
              "application/rtf": [".rtf"],
              "application/msword": [".doc"],
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            }}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className={styles.dropZone}>
                <input {...getInputProps()} />
                <UploadIcon fontSize="large" className={styles.uploadIconColor} />
                <p>
                  Drag and drop here
                  <br />
                  <span className={styles.dropZoneCta}>or click to browse files</span>
                  <br />
                  Accepted file types: .doc, .docx, .txt, .rtf
                </p>
              </div>
            )}
          </Dropzone>
        </Box>
        <Box component="form" sx={{ padding: "20px" }}>
          <Typography sx={{ marginBottom: "14px", fontWeight: "bold" }}>Enter a document</Typography>
          <TextField
            id="document-name"
            placeholder="Document name"
            value={uploadDocName}
            onChange={(e) => setUploadDocName(e.target.value)}
            sx={{ marginBottom: "10px" }}
            fullWidth
          />
          <TextField
            id="document-content"
            value={uploadTextfield}
            onChange={(e) => setUploadTextfield(e.target.value)}
            multiline
            placeholder="Copy and paste, or type document here"
            rows={6}
            fullWidth
          />
        </Box>
        <DialogActions>
          <Button onClick={closeUploadModal}>Cancel</Button>
          <Button onClick={uploadDocumentText} disabled={!uploadTextfield}>
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>

      <UploadAudioModal
        open={uploadAudioModalOpen}
        setOpen={setUploadAudioModalOpen}
        showSnackbar={showSnackbar}
        refreshCallback={() => { setPage(0); fetchDocs(false, 0); }}
      />
      {deleteConfirm}
      {editTitleDialog}
      {deleteTapConfirm}
      {snackbarEl}
    </div>
  );
};

// ── Document Card ──

const DocumentCard = ({ doc, deleteDoc, editTitle, setCreateTapModalOpen, setCreatingDoc, shareDoc, deleteTap, retryDoc, retrying }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [shareTap, setShareTap] = useState(null);
  const [tapsPerRow, setTapsPerRow] = useState(TAPS_PER_ROW);
  const tapsGridRef = useRef();

  // Measure how many taps fit in one row after images load
  useEffect(() => {
    const grid = tapsGridRef.current;
    if (!grid || !doc.taps.length) return;

    function measureRow() {
      const children = Array.from(grid.children);
      if (!children.length) return;
      const firstTop = children[0].offsetTop;
      let count = 0;
      for (const child of children) {
        if (child.offsetTop === firstTop) count++;
        else break;
      }
      if (count > 0) setTapsPerRow(count);
    }

    // Measure after images load
    const images = grid.querySelectorAll("img");
    let loaded = 0;
    const total = images.length;
    if (!total) { measureRow(); return; }

    function onLoad() {
      loaded++;
      if (loaded >= total) measureRow();
    }
    images.forEach((img) => {
      if (img.complete) { loaded++; } else { img.addEventListener("load", onLoad); }
    });
    if (loaded >= total) measureRow();

    // Also remeasure on resize
    const observer = new ResizeObserver(measureRow);
    observer.observe(grid);

    return () => {
      observer.disconnect();
      images.forEach((img) => img.removeEventListener("load", onLoad));
    };
  }, [doc.taps]);

  const overflowTaps = doc.taps.slice(tapsPerRow);
  const hasOverflow = overflowTaps.length > 0;

  const renderTap = (tap) => (
    <div className={styles.tapLink} key={tap.tapId}>
      <Link to={"/tapestry/" + tap.tapId}>
        <img
          className={styles.tapImg}
          src={tap.thumbnailUrl}
          alt={tap.title || "tapestry"}
          onError={retryTapImageOtherExt}
        />
      </Link>
      <div className={styles.tapActions}>
        <div
          className={styles.tapActionBtn}
          onClick={(e) => {
            e.stopPropagation();
            setShareTap({ url: tap.imageUrl, title: tap.title });
          }}
          title="Share image">
          <ShareIcon />
        </div>
        {canEditDoc(doc) && (
          <div
            className={styles.tapActionBtnDanger}
            onClick={(e) => {
              e.stopPropagation();
              deleteTap(tap.tapId, tap.title);
            }}
            title="Delete">
            <CloseIcon />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.docCard}>
      {/* Header */}
      <div className={styles.docHeader}>
        <Link to={`/document/${doc.docId}`} className={styles.docTitleLink}>
          <div className={styles.docTitle} title={doc.title}>{doc.title}</div>
        </Link>
        <span className={styles.docDate}>
          <span className={styles.dateFull}>{doc.created.format("MMM D, YYYY")}</span>
          <span className={styles.dateShort}>{doc.created.format("M/D/YY")}</span>
        </span>
        {doc.shared && (
          <span className={styles.sharedBadge} title={`Shared with you by ${sharedByLabel(doc)}`}>
            <PeopleIcon /> {sharedByLabel(doc)}
          </span>
        )}
        <div className={styles.docActions}>
          {canEditDoc(doc) && (
            <button
              className={styles.iconBtn}
              title="Edit title"
              onClick={() => editTitle(doc.title, doc.docId)}>
              <EditIcon />
            </button>
          )}
          {!doc.shared && (
            <>
              <button className={styles.iconBtn} title="Share document" onClick={() => shareDoc(doc)}>
                <PersonAddIcon />
              </button>
              <button
                className={styles.iconBtnDanger}
                title="Delete document"
                onClick={() => deleteDoc(doc.title, doc.docId)}>
                <DeleteOutlineIcon />
              </button>
            </>
          )}
          {!doc.shared && canRetry(doc.spoken?.failure) && (
            <button
              className={styles.iconBtn}
              title="Retry document"
              disabled={retrying}
              onClick={() => retryDoc(doc)}>
              <RefreshIcon />
            </button>
          )}
          <div className={styles.createTapSpacer} />
          <button
            className={styles.liveTapBtn}
            title="Open Live Tap"
            onClick={() => navigate(`/live-tap/${doc.docId}`)}>
            <SensorsIcon />
          </button>
          <div className={styles.createTapSpacer} />
          {canEditDoc(doc) && (
            <button
              className={styles.createTapBtn}
              title="Create Tapestry"
              onClick={() => {
                setCreatingDoc(doc);
                setCreateTapModalOpen(true);
              }}>
              <PaletteIcon />
            </button>
          )}
        </div>
      </div>

      {/* Tapestries */}
      {doc.taps.length > 0 && (
        <div className={styles.tapsContainer}>
          <div ref={tapsGridRef} className={`${styles.tapsGrid} ${expanded ? styles.tapsGridExpanded : styles.tapsGridCollapsed}`}>
            {doc.taps.map(renderTap)}
          </div>
          {hasOverflow && (
            <button className={styles.showMoreBtn} onClick={() => {
              if (expanded && tapsGridRef.current) {
                tapsGridRef.current.scrollTo({ top: 0, behavior: "smooth" });
              }
              setExpanded(!expanded);
            }}>
              {expanded ? <><ExpandLessIcon style={{ fontSize: 16 }} /> Show less</> : <><ExpandMoreIcon style={{ fontSize: 16 }} /> {overflowTaps.length} more</>}
            </button>
          )}
        </div>
      )}
      {doc.taps.length === 0 && (
        <div className={styles.tapsContainer}>
          <span className={styles.noTaps}>No tapestries yet</span>
        </div>
      )}

      <ShareModal
        open={!!shareTap}
        handleClose={() => setShareTap(null)}
        tapShareUrl={shareTap?.url}
        title={shareTap?.title}
      />
    </div>
  );
};

DocumentCard.propTypes = {
  doc: PropTypes.object,
  deleteDoc: PropTypes.func,
  editTitle: PropTypes.func,
  setCreateTapModalOpen: PropTypes.func,
  setCreatingDoc: PropTypes.func,
  shareDoc: PropTypes.func,
  deleteTap: PropTypes.func,
  retryDoc: PropTypes.func,
  retrying: PropTypes.bool,
};

// ── Document Row (list view) ──

const DocumentRow = ({ doc, deleteDoc, editTitle, setCreateTapModalOpen, setCreatingDoc, shareDoc, retryDoc, retrying }) => {
  const navigate = useNavigate();
  return (
    <div className={styles.docRow}>
      <Link to={`/document/${doc.docId}`} className={styles.docRowTitleLink}>
        <span className={styles.docRowTitle} title={doc.title}>{doc.title}</span>
      </Link>
      <span className={styles.docRowTapCount}>
        {doc.taps.length} {doc.taps.length === 1 ? "tap" : "taps"}
      </span>
      <span className={styles.docRowDate}>
        <span className={styles.dateFull}>{doc.created.format("MMM D, YYYY")}</span>
        <span className={styles.dateShort}>{doc.created.format("M/D/YY")}</span>
      </span>
      {doc.shared && (
        <span className={styles.sharedBadge} title={`Shared with you by ${sharedByLabel(doc)}`}>
          <PeopleIcon /> {sharedByLabel(doc)}
        </span>
      )}
      <div className={styles.docRowActions}>
        {canEditDoc(doc) && (
          <button
            className={styles.iconBtn}
            title="Edit title"
            onClick={() => editTitle(doc.title, doc.docId)}>
            <EditIcon />
          </button>
        )}
        {!doc.shared && (
          <>
            <button className={styles.iconBtn} title="Share document" onClick={() => shareDoc(doc)}>
              <PersonAddIcon />
            </button>
            <button
              className={styles.iconBtnDanger}
              title="Delete document"
              onClick={() => deleteDoc(doc.title, doc.docId)}>
              <DeleteOutlineIcon />
            </button>
          </>
        )}
        {!doc.shared && canRetry(doc.spoken?.failure) && (
          <button
            className={styles.iconBtn}
            title="Retry document"
            disabled={retrying}
            onClick={() => retryDoc(doc)}>
            <RefreshIcon />
          </button>
        )}
        <div className={styles.createTapSpacer} />
        <button
          className={styles.liveTapBtn}
          title="Open Live Tap"
          onClick={() => navigate(`/live-tap/${doc.docId}`)}>
          <SensorsIcon />
        </button>
        <div className={styles.createTapSpacer} />
        {canEditDoc(doc) && (
          <button
            className={styles.createTapBtn}
            title="Create Tapestry"
            onClick={() => {
              setCreatingDoc(doc);
              setCreateTapModalOpen(true);
            }}>
            <PaletteIcon />
          </button>
        )}
      </div>
    </div>
  );
};

DocumentRow.propTypes = {
  doc: PropTypes.object,
  deleteDoc: PropTypes.func,
  editTitle: PropTypes.func,
  setCreateTapModalOpen: PropTypes.func,
  setCreatingDoc: PropTypes.func,
  shareDoc: PropTypes.func,
  retryDoc: PropTypes.func,
  retrying: PropTypes.bool,
};

export default UnifiedDocumentsPage;
